import React from "react";
import moment from "moment";
import { ChevronDown, Clock3, Eye, Gauge, Send, Video } from "lucide-react";
import { useLocation } from "react-router-dom";
import { MdLocationPin, MdOutlineMyLocation, MdVpnKey } from "react-icons/md";

const getFromNow = (timestamp) => {
  if (!timestamp) return "No Data";
  return moment.unix(timestamp).fromNow();
};

const buildQueryPath = (basePath, vehicleUniqueId, locationSearch, extraParams = {}) => {
  const params = new URLSearchParams();
  const currentSearchParams = new URLSearchParams(locationSearch || "");
  const token = currentSearchParams.get("token");

  if (vehicleUniqueId) {
    params.set("device", vehicleUniqueId);
  }

  if (token !== null) {
    params.set("token", token);
  }

  Object.entries(extraParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.set(key, String(value));
    }
  });

  const search = params.toString();
  return search ? `${basePath}?${search}` : basePath;
};

export const VehicleItem = ({
  vehicle,
  navigate,
  checked = false,
  onCheckboxChange,
  compact = false,
  showDetailsToggle = false,
  detailsOpen = false,
  onToggleDetails,
  showActionButtons = true,
}) => {
  const location = useLocation();
  const normalizedPathname = String(location.pathname || "").toLowerCase();
  const isTrackingV2Route = normalizedPathname.includes("/tracking-v2");
  const isV2Route =
    isTrackingV2Route ||
    normalizedPathname.includes("/event-center-v2") ||
    normalizedPathname.includes("/dashcam-v2");
  const eventCenterPath = isV2Route ? "/my-data/event-center-v2" : "/my-data/event-center";
  const dashcamPath = isV2Route ? "/my-data/dashcam-v2" : "/my-data/dashcam";
  const actionButtons = [
    {
      title: "Cle d'acces",
      icon: MdVpnKey,
      color: "text-blue-600",
      bg: "bg-blue-100",
      path: buildQueryPath("#", vehicle?.uniqueId, location.search),
    },
    {
      title: "Localisation",
      icon: MdLocationPin,
      color: "text-indigo-600",
      bg: "bg-indigo-100",
      path: buildQueryPath("#", vehicle?.uniqueId, location.search),
    },
    {
      title: "Event center",
      icon: MdOutlineMyLocation,
      color: "text-emerald-600",
      bg: "bg-emerald-100",
      path: buildQueryPath(eventCenterPath, vehicle?.uniqueId, location.search),
    },
    {
    title: "Dashcam",
    icon: Video,
    color: "text-violet-600",
    bg: "bg-violet-100",
    path: buildQueryPath(dashcamPath, vehicle?.uniqueId, location.search)
  },
  ];
 


  if (isTrackingV2Route) {
    actionButtons.push(
      {
        title: "Preview event",
        icon: Eye,
        color: "text-sky-600",
        bg: "bg-sky-100",
        path: buildQueryPath(eventCenterPath, vehicle?.uniqueId, location.search, {
          isopen: true,
        }),
      },
      {
        title: "Track event",
        icon: Send,
        color: "text-amber-600",
        bg: "bg-amber-100",
        path: buildQueryPath(eventCenterPath, vehicle?.uniqueId, location.search, {
          isevent: true,
        }),
      }
    );
  }


  const rawStatus = vehicle.status
    ? String(vehicle.status).toLowerCase()
    : vehicle.isOffline
      ? "offline"
      : vehicle.isNoGps
        ? "nogps"
        : vehicle.haltStatus
          ? "halt"
          : vehicle.idlingStatus === true
            ? "idle"
            : vehicle.idlingStatus === false && vehicle.haltStatus === false
              ? "running"
              : "default";

  const statusLabel =
    rawStatus === "running"
      ? "En marche"
      : rawStatus === "idle"
        ? "Ralenti"
        : rawStatus === "halt"
          ? "Arrete"
          : rawStatus === "offline"
            ? "Hors ligne"
            : rawStatus === "nogps"
              ? "Sans GPS"
              : vehicle.status || "Arrete";

  const statusTone =
    rawStatus === "running"
      ? {
          soft: "bg-green-50 text-green-600",
          strong: "bg-green-500 text-white",
        }
      : rawStatus === "idle"
        ? {
            soft: "bg-amber-50 text-amber-600",
            strong: "bg-amber-500 text-white",
          }
        : rawStatus === "offline" || rawStatus === "nogps"
          ? {
              soft: "bg-slate-100 text-slate-600",
              strong: "bg-slate-500 text-white",
            }
          : {
              soft: "bg-red-50 text-red-500",
              strong: "bg-red-500 text-white",
            };

  if (compact) {
    return (
      <div className="w-full rounded-lg border border-slate-200 bg-white/95 px-2 py-1 shadow-[0_4px_10px_rgba(15,23,42,0.06)]">
        <div className="flex items-center gap-2">
          <div className="flex min-w-0 items-center gap-1">
            <input
              type="checkbox"
              checked={checked}
              onChange={onCheckboxChange}
              onClick={(e) => e.stopPropagation()}
              className="h-3.5 w-3.5 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            {showDetailsToggle ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleDetails?.();
                }}
                className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded text-slate-500 hover:bg-slate-100"
                title={detailsOpen ? "Masquer les details" : "Afficher les details"}
              >
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${
                    detailsOpen ? "rotate-180" : "rotate-0"
                  }`}
                />
              </button>
            ) : null}
            <span className={`${showActionButtons ? "max-w-[118px] sm:max-w-[132px]" : "min-w-0 flex-1"} truncate text-[12px] font-bold uppercase tracking-[0.01em] text-slate-900`}>
              {vehicle.vehicleNumber}
            </span>
          </div>

          {showActionButtons ? (
            <div className="flex shrink-0 items-center gap-0.5 rounded-full border border-slate-100 bg-slate-50/90 px-1 py-0.5">
              {actionButtons.map(({ title, icon: IconComponent, color, bg, path }, index) => (
                <button
                  key={index}
                  title={title}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(path);
                  }}
                  className={`inline-flex h-[18px] w-[18px] items-center justify-center rounded-full ${bg} ${color} transition hover:scale-105`}
                >
                  <IconComponent className="h-3 w-3" />
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full rounded-2xl border border-slate-200 bg-white/95 px-2.5 py-2 shadow-[0_10px_24px_rgba(15,23,42,0.08)] transition hover:shadow-[0_12px_28px_rgba(15,23,42,0.12)]">
      <div className="mb-1.5 flex items-start justify-between gap-2">
        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={checked}
            onChange={onCheckboxChange}
            onClick={(e) => e.stopPropagation()}
            className="mt-1 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
          />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="truncate text-[13px] font-bold uppercase tracking-[0.01em] text-slate-900">
                {vehicle.vehicleNumber}
              </span>
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-bold ${statusTone.soft}`}>
                {statusLabel}
              </span>
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px] text-slate-600">
              <span className="inline-flex items-center gap-1 font-semibold text-slate-800">
                <Gauge className="h-3.5 w-3.5 text-slate-500" />
                {vehicle.speed ? `${vehicle.speed} km/h` : "0 km/h"}
              </span>
              <span className="inline-flex items-center gap-1">
                <Clock3 className="h-3.5 w-3.5 text-slate-500" />
                {vehicle.timestamp ? `il y a ${getFromNow(vehicle.timestamp)}` : "No Data"}
              </span>
            </div>
          </div>
        </div>

        <span className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold shadow-sm ${statusTone.strong}`}>
          {statusLabel}
        </span>
      </div>

      {showActionButtons ? (
        <>
          <div className="mb-1.5 h-px w-full bg-slate-100" />

          <div className="flex flex-wrap items-center gap-1.5">
            {actionButtons.map(({ title, icon: IconComponent, color, bg, path }, index) => (
              <button
                key={index}
                title={title}
                onClick={() => navigate(path)}
                className={`inline-flex h-[18px] w-[18px] items-center justify-center rounded-full ${bg} ${color} transition hover:scale-105`}
              >
                <IconComponent className="h-3 w-3" />
              </button>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default VehicleItem;
